import { trigger } from '@angular/animations';
import { Location, isPlatformBrowser, isPlatformServer } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  ViewChild,
  computed,
  inject,
  linkedSignal,
  signal,
  viewChildren,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { AssetColorLabel, AssetDto, AssetStatus, TenantDto } from '@nusign/picflow-backend-types';
import {
  Subject,
  catchError,
  combineLatest,
  delay,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  firstValueFrom,
  forkJoin,
  from,
  map,
  of,
  pairwise,
  race,
  retry,
  shareReplay,
  switchMap,
  switchScan,
  take,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';
import { fadeIf } from '../../../animations/fade.if.animation';
import { moveDownIf } from '../../../animations/move-down.if.animation';
import { moveLeftArrowIf } from '../../../animations/move-left-arrow.if.animation';
import { moveRightArrowIf } from '../../../animations/move-right-arrow.if.animation';
import { moveUpIf } from '../../../animations/move-up.if.animation';
import { billingScopes } from '../../../billing-consts';
import { Rect } from '../../../classes/rect.class';
import { defaultColorLabels } from '../../../configs/default-color-labels';
import { assetPaginationThreshold, baseZoom, doubleClickTime, longPressDuration } from '../../../consts';
import { AssetPageBlurHashComponent } from '../../../gallery/components/asset-page-blur-hash/asset-page-blur-hash.component';
import { AssetPageItemComponent } from '../../../gallery/components/asset-page-item/asset-page-item.component';
import { DocumentViewerControlsComponent } from '../../../gallery/components/document-viewer-controls/document-viewer-controls.component';
import { PdfPageListComponent } from '../../../gallery/components/pdf-page-list/pdf-page-list.component';
import { VideoViewerControlsComponent } from '../../../gallery/components/video-viewer-controls/video-viewer-controls.component';
import { IosDownloadDialogComponent } from '../../../gallery/dialogs/ios-download-dialog/ios-download-dialog.component';
import { calcLegacyTheme } from '../../../gallery/lib/calc-legacy-theme';
import { getAssetVersionDimensions } from '../../../gallery/lib/get-asset-version-dimensions';
import { showIosDownloadMenu } from '../../../gallery/lib/show-ios-download-menu';
import { sortAssetSection } from '../../../gallery/lib/sort-asset-section';
import { AssetTransitionService } from '../../../gallery/services/asset-transition.service';
import { GROWTHBOOK } from '../../../growthbook';
import { canDisplayAsset } from '../../../lib/can-display-asset';
import { copy } from '../../../lib/copy';
import { debounce } from '../../../lib/debounce';
import { devicePixelRatio } from '../../../lib/device-pixel-ratio';
import { elementIsInputLike } from '../../../lib/element-is-input-like';
import { getAllRouteParams } from '../../../lib/get-all-route-params';
import { getEventPath } from '../../../lib/get-event-path';
import { getFullPath } from '../../../lib/get-full-path';
import { getTagFromEvent } from '../../../lib/get-tag-from-event';
import { includeIsLiked } from '../../../lib/include-is-liked';
import { IsIos } from '../../../lib/is-ios';
import { isMetaKey } from '../../../lib/is-meta-key';
import { isTouchDevice } from '../../../lib/is-touch-device';
import { objectsMatch } from '../../../lib/objects-match';
import { sleep } from '../../../lib/sleep';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { ActivityService } from '../../../services/activity.service';
import { AssetDisplayService } from '../../../services/asset-display.service';
import { AssetFeedbackService } from '../../../services/asset-feedback.service';
import { AssetService } from '../../../services/asset.service';
import { CommentService } from '../../../services/comment.service';
import { ContextMenuService } from '../../../services/context-menu.service';
import { DialogService } from '../../../services/dialog.service';
import { DownloadAssetService } from '../../../services/download-asset.service';
import { GalleryService } from '../../../services/gallery.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { StateService } from '../../../services/state.service';
import { StorageService } from '../../../services/storage.service';
import { ToastService } from '../../../services/toast.service';
import { UnseenActivityService } from '../../../services/unseen-activity.service';
import { UploadService } from '../../../services/upload.service';
import { VideoTimeService } from '../../../services/video-time.service';
import { DropComponent } from '../../../shared-extra/components/drop/drop.component';
import { CircleProgressBarComponent } from '../../../shared/components/circle-progress-bar/circle-progress-bar.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { AnnotationControlsComponent } from '../../components/annotation-controls/annotation-controls.component';
import { AssetCommentFacesComponent } from '../../components/asset-comment-faces/asset-comment-faces.component';
import { AssetFeedbackHintsComponent } from '../../components/asset-feedback-hints/asset-feedback-hints.component';
import { AssetHeaderComponent } from '../../components/asset-header/asset-header.component';
import { AssetInfoComponent } from '../../components/asset-info/asset-info.component';
import { AssetPageFooterComponent } from '../../components/asset-page-footer/asset-page-footer.component';
import { AssetSideMenuComponent } from '../../components/asset-side-menu/asset-side-menu.component';
import { CommentThread, CommentsComponent } from '../../components/comments/comments.component';
import { CreateAnnotationComponent } from '../../components/create-annotation/create-annotation.component';
import { CreateCommentOverlayComponent } from '../../components/create-comment-overlay/create-comment-overlay.component';
import { ImageViewerControlsComponent } from '../../components/image-viewer-controls/image-viewer-controls.component';
import { ImageViewerPreviewComponent } from '../../components/image-viewer-preview/image-viewer-preview.component';
import { ImageViewerComponent } from '../../components/image-viewer/image-viewer.component';
import { ThumbnailBarComponent } from '../../components/thumbnail-bar/thumbnail-bar.component';
import { VideoTimeData } from '../../components/video-viewer/video-viewer.component';
import { ViewAnnotationComponent } from '../../components/view-annotation/view-annotation.component';
import { Filter } from '../../interfaces/filter.interface';
import { calcContainerSizeForAsset } from '../../lib/calc-container-size-for-asset';
import { filterAssets } from '../../lib/filter-assets';
import { getAnnotationBounds } from '../../lib/get-annotation-bounds';
import {
  getPointCommentPosition,
  getShapeCommentPosition,
  relativeToAbsolutePosition,
} from '../../lib/get-annotation-positions';
import { getAssetGroups } from '../../lib/sort-assets-sorting-and-grouping';
import { CanDisplayAssetPipe } from '../../pipes/can-display-asset.pipe';
import { CropPipe } from '../../pipes/crop.pipe';
import { OffsetScalePipe } from '../../pipes/offset-scale.pipe';
import { ScalePipe } from '../../pipes/scale.pipe';
import { AnnotationColor } from '../../types/annotation-color';
import { AnnotationTool } from '../../types/annotation-tool';
import { Shape, SimpleAnnotation } from '../../types/shapes';

@Component({
  selector: 'app-asset-page',
  templateUrl: './asset-page.component.html',
  styleUrls: ['./asset-page.component.scss'],
  animations: [
    trigger('arrowLeft', moveLeftArrowIf),
    trigger('arrowRight', moveRightArrowIf),
    trigger('assetHeader', moveDownIf),
    trigger('assetFooter', moveUpIf),
    trigger('assetControls', moveDownIf),
    trigger('thumbnailBar', moveUpIf),
    trigger('assetFeedbackHints', fadeIf),
    trigger('animationMode', fadeIf),
    trigger('longPressMessage', fadeIf),
    trigger('sectionBackground', fadeIf),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropComponent,
    AssetHeaderComponent,
    ResizeDirective,
    ImageViewerControlsComponent,
    SvgComponent,
    LoadingIndicatorComponent,
    ImageViewerPreviewComponent,
    CreateAnnotationComponent,
    ViewAnnotationComponent,
    AssetPageFooterComponent,
    AnnotationControlsComponent,
    AssetFeedbackHintsComponent,
    AssetSideMenuComponent,
    AssetInfoComponent,
    // ThumbnailBarComponent,
    VideoViewerControlsComponent,
    CanDisplayAssetPipe,
    CommentsComponent,
    CreateCommentOverlayComponent,
    AssetCommentFacesComponent,
    PdfPageListComponent,
    DocumentViewerControlsComponent,
    AssetPageItemComponent,
    OffsetScalePipe,
    CropPipe,
    ScalePipe,
    ThumbnailBarComponent,
    ImageViewerComponent,
    AssetPageBlurHashComponent,
    CircleProgressBarComponent,
  ],
})
export class AssetPageComponent implements OnInit, OnDestroy {
  private check = inject(ChangeDetectorRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private location = inject(Location);
  private state = inject(StateService);
  private feedbackService = inject(AssetFeedbackService);
  private assetService = inject(AssetService);
  private sectionService = inject(SectionService);
  private assetDisplayService = inject(AssetDisplayService);
  private galleryService = inject(GalleryService);
  private platformId = inject<string>(PLATFORM_ID);
  private uploadService = inject(UploadService);
  private toasts = inject(ToastService);
  private storage = inject(StorageService);
  private contextService = inject(ContextMenuService);
  private dialog = inject(DialogService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private videoTimeService = inject(VideoTimeService);
  public commentService = inject(CommentService);
  private permissionService = inject(PermissionService);
  private assetTransitionService = inject(AssetTransitionService);
  private downloadAssetService = inject(DownloadAssetService);
  private unseenActivityService = inject(UnseenActivityService);
  private activityService = inject(ActivityService);
  public growthBook = inject(GROWTHBOOK);

  @ViewChild('createAnnotation', { static: false }) createAnnotation: CreateAnnotationComponent;
  @ViewChild('comments', { static: false }) comments: CommentsComponent;
  // private readonly activeAssetEl = viewChild<AssetPageItemComponent>('activeAssetEl');
  private readonly assetPageItems = viewChildren(AssetPageItemComponent);
  readonly activeVideoEl = computed(() => {
    const assetPageItems = this.assetPageItems();
    const index = this.renderedAssetsActiveIndex();
    if (!assetPageItems || index === undefined) return;
    return assetPageItems[index]?.videoViewer();
  });

  private readonly gallery$ = this.route.params.pipe(
    switchMap(() => {
      const params = getAllRouteParams(this.route.snapshot);
      const idOrPath = params.galleryPath || params.galleryId;
      if (idOrPath) {
        return from(this.galleryService.getPublicGalleryFromSnapshot(this.route.snapshot));
      }
      return this.assetService.getAsset(params.assetId, undefined, { allowCache: true }).pipe(
        take(1),
        switchMap(asset => this.galleryService.getGallery(asset.gallery as string).pipe(take(1)))
      );
    }),
    switchMap(gallery => this.galleryService.withUpdates([gallery])),
    map(galleries => galleries[0]),
    shareReplay(1)
  );

  readonly gallery = toSignal(this.gallery$);

  readonly theme = computed(() => {
    if (!this.gallery()) return;
    return this.gallery().theme || calcLegacyTheme(this.gallery());
  });
  readonly galleryPermissions$ = this.gallery$.pipe(
    filter(Boolean),
    distinctUntilKeyChanged('id'),
    switchMap(gallery => this.galleryService.getGalleryPermissions(gallery))
  );
  readonly galleryPermissions = toSignal(this.galleryPermissions$);
  readonly isTeamMember = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    return !!this.state.tenants?.find(t => t.id === tenantId);
  });
  backupZoomSettings: { scale: number; offset: Rect };
  readonly dragging = signal(false);
  previousTouches: Rect[];

  readonly destroyed$ = new Subject<void>();

  private readonly assetAndGalleryId$ = this.route.params.pipe(
    map(params => params.assetId),
    distinctUntilChanged(),
    switchMap(assetId =>
      this.gallery$.pipe(
        filter(Boolean),
        distinctUntilKeyChanged('id'),
        map(gallery => ({ galleryId: gallery.id, assetId }))
      )
    ),
    shareReplay(1)
  );

  readonly sideMenuMode = toSignal(
    this.route.queryParams.pipe(
      map(q => q.menu as 'info' | 'activity'),
      distinctUntilChanged()
    )
  );

  readonly assetTransitionMode = toSignal(this.assetTransitionService.onAssetChange.pipe(map(o => !!o)));

  readonly showWebGl = computed(() => {
    if (this.growthBook.isOn('no-webgl')) return false;
    const asset = this.activeAsset();
    const scale = this.scale();
    const tempDisabled = this.tempDisabledWebGlAnimations();
    const animateOffset = this.animateOffset();
    if (!asset) return false;
    if (tempDisabled && animateOffset) return false;
    if (asset.type !== 'image') return false;
    return scale > 1;
  });

  private readonly sections$ = this.assetAndGalleryId$.pipe(
    switchMap(({ galleryId }) => this.sectionService.getSections(galleryId)),
    shareReplay(1)
  );

  private readonly activeAsset$ = this.assetAndGalleryId$.pipe(
    switchMap(({ assetId, galleryId }) =>
      forkJoin([this.galleryPermissions$.pipe(filter(Boolean), take(1)), this.gallery$.pipe(take(1))]).pipe(
        switchMap(([permissions, gallery]) => {
          return this.assetService
            .getAsset(assetId, galleryId, { allowCache: true, includeIsLiked: includeIsLiked(permissions, gallery) })
            .pipe(take(1));
        })
      )
    ),
    switchMap(asset => this.assetService.withUpdates([asset])),
    map(assets => assets[0]),
    tap(asset => {
      if (!asset) {
        // asset was probably deleted
        const previousAsset = this.previousAsset();
        const nextAsset = this.nextAsset();
        if (previousAsset) {
          this.changeToAsset(previousAsset.id);
        } else if (nextAsset) {
          this.changeToAsset(nextAsset.id);
        } else {
          this.close();
        }
      }
    }),
    shareReplay(1)
  );
  readonly activeAsset = toSignal(this.activeAsset$);

  readonly fetchBeforeIncrement = 20;
  readonly fetchAfterIncrement = 50;
  readonly minBuffer = 5;

  readonly fetchBeforeCount = signal(this.fetchBeforeIncrement);
  readonly fetchAfterCount = signal(this.fetchAfterIncrement);

  private reachedStart = signal(false);
  private reachedEnd = signal(false);
  private loadingMoreAssets = signal(false);

  // https://www.figma.com/board/FTt0cTbsiClk0UaetH2GOp/Asset-Page-fetching-logic?node-id=0-1&t=14HXMokXczot8hwm-1
  private readonly assetList$ = combineLatest([
    this.assetAndGalleryId$,
    toObservable(this.fetchBeforeCount),
    toObservable(this.fetchAfterCount),
    toObservable(this.galleryPermissions).pipe(filter(Boolean), distinctUntilChanged(objectsMatch)),
    toObservable(this.gallery).pipe(filter(Boolean), distinctUntilKeyChanged('id')),
  ]).pipe(
    switchScan(
      (prevItems, [{ assetId, galleryId }, fetchBeforeCount, fetchAfterCount, galleryPermissions, gallery]) => {
        this.loadingMoreAssets.set(true);

        return this.assetService
          .getAsset(assetId, galleryId, {
            allowCache: true,
            includeIsLiked: includeIsLiked(galleryPermissions, gallery),
          })
          .pipe(
            take(1),
            switchMap(activeAsset => {
              // handle /a/asset/:id
              const params = getAllRouteParams(this.route.snapshot);
              if (!params.galleryPath && !params.galleryId) return of([activeAsset]);
              if (isPlatformServer(this.platformId)) return of([activeAsset]);

              return this.sections$.pipe(
                take(1),
                switchMap(sections => {
                  const queryParams = this.route.snapshot.queryParams;
                  const filtersString = queryParams.filters;
                  const search = queryParams.search;
                  const sorting = queryParams.sorting;
                  const grouping = queryParams.grouping;
                  const anySectionHasCaptureTimeSorting = sections.some(s => s.sorting.endsWith('capture_time'));

                  if (
                    filtersString ||
                    search ||
                    sorting ||
                    grouping ||
                    anySectionHasCaptureTimeSorting ||
                    gallery.assets_count <= assetPaginationThreshold // if the gallery is small, just fetch all assets
                  ) {
                    const isAdvancedMode = getFullPath(this.route.snapshot).includes('advanced');
                    return forkJoin([
                      this.assetService
                        .getAssetsForGalleryObservable(galleryId, {
                          includeIsLiked: includeIsLiked(galleryPermissions, gallery),
                          includeTags: isAdvancedMode,
                        })
                        .pipe(take(1)),
                      this.gallery$.pipe(take(1)),
                    ]).pipe(
                      switchMap(([assets, gallery]) => {
                        return this.activityService
                          .getGalleryAssetUserFeedbackMap(gallery, assets)
                          .pipe(map(feedbackMap => ({ assets, gallery, feedbackMap })));
                      }),
                      map(({ assets, gallery, feedbackMap }) => {
                        const canUseCustomColorLabels = this.permissionService.canDisplayElement(
                          billingScopes.asset.customLabels
                        );
                        const colorLabels = canUseCustomColorLabels ? gallery.color_labels : defaultColorLabels;

                        const groups = getAssetGroups(assets, grouping || 'section', sections, colorLabels);
                        const allAssets: AssetDto[] = [];
                        groups.forEach(group => {
                          if (!group.assets.length) return;

                          const groupSorting = (() => {
                            if (sorting) return sorting;
                            const sectionId = group.assets[0].section;
                            const section = sections.find(s => s.id === sectionId);
                            return section.sorting;
                          })();
                          const sortedGroup = sortAssetSection(group.assets, groupSorting);
                          allAssets.push(...sortedGroup);
                        });

                        const filters: Filter[] = [];
                        if (filtersString) {
                          try {
                            filters.push(...JSON.parse(filtersString));
                          } catch (e) {
                            console.error('Invalid filters JSON', e);
                          }
                        }

                        return filterAssets(
                          allAssets,
                          feedbackMap,
                          filters || [],
                          search,
                          false,
                          this.state.unseenActivityCountMap
                        );
                      })
                    );
                  }

                  const index = prevItems.findIndex(i => i.id === assetId);
                  const currentBefore = index > -1 ? prevItems.slice(0, index) : [];
                  const extraBeforeToFetch = Math.max(0, fetchBeforeCount - currentBefore.length);
                  const canReuseBefore = this.reachedStart() || extraBeforeToFetch <= this.minBuffer;
                  const firstBefore = currentBefore[0] || activeAsset;

                  const getBefore = canReuseBefore
                    ? of(currentBefore)
                    : this.assetService
                        .getNextAssets(
                          firstBefore,
                          firstBefore.section as string,
                          sections,
                          firstBefore.gallery as string,
                          fetchBeforeCount,
                          'before',
                          { includeIsLiked: includeIsLiked(galleryPermissions, gallery) }
                        )
                        .pipe(
                          tap(assets => this.reachedStart.set(assets.length < fetchBeforeCount)),
                          map(beforeAssets => [...beforeAssets, ...currentBefore])
                        );

                  const currentAfter = index > -1 ? prevItems.slice(index + 1) : [];
                  const extraAfterToFetch = Math.max(0, fetchAfterCount - currentAfter.length);
                  const canReuseAfter = this.reachedEnd() || extraAfterToFetch <= this.minBuffer;
                  const lastAfter = currentAfter[currentAfter.length - 1] || activeAsset;

                  const getAfter = canReuseAfter
                    ? of(currentAfter)
                    : this.assetService
                        .getNextAssets(
                          lastAfter,
                          lastAfter.section as string,
                          sections,
                          lastAfter.gallery as string,
                          fetchAfterCount,
                          'after',
                          { includeIsLiked: includeIsLiked(galleryPermissions, gallery) }
                        )
                        .pipe(
                          tap(assets => this.reachedEnd.set(assets.length < fetchAfterCount)),
                          map(afterAssets => [...currentAfter, ...afterAssets])
                        );

                  return forkJoin([getBefore, getAfter]).pipe(
                    map(([beforeAssets, afterAssets]) => [...beforeAssets, activeAsset, ...afterAssets])
                  );
                })
              );
            }),
            tap(() => this.loadingMoreAssets.set(false)),
            switchMap(assets => this.assetService.withUpdates(assets))
          );
      },
      [] as AssetDto[]
    ),
    shareReplay(1)
  );

  readonly assetList = toSignal(this.assetList$);

  readonly previousAsset = computed(() => {
    const assetList = this.assetList();
    const activeAsset = this.activeAsset();
    if (!assetList || !activeAsset) return;
    return assetList[assetList.findIndex(a => a.id === activeAsset.id) - 1];
  });

  readonly nextAsset = computed(() => {
    const assetList = this.assetList();
    const activeAsset = this.activeAsset();
    if (!assetList || !activeAsset) return;
    return assetList[assetList.findIndex(a => a.id === activeAsset.id) + 1];
  });

  readonly renderedAssets: Signal<AssetDto[]> = toSignal(
    this.assetAndGalleryId$.pipe(
      switchMap(({ assetId }) => {
        return this.assetList$.pipe(
          map(assets => {
            const index = assets.findIndex(a => a.id === assetId);
            const maxSecondaryBefore = 1;
            const maxSecondaryAfter = 3;
            return assets.slice(Math.max(index - maxSecondaryBefore, 0), index + maxSecondaryAfter + 1);
          })
        );
      })
    )
  );

  readonly renderedAssetsActiveIndex = computed(() => {
    const renderedAssets = this.renderedAssets();
    const activeAsset = this.activeAsset();
    if (!renderedAssets || !activeAsset) return;
    return renderedAssets.findIndex(a => a.id === activeAsset.id);
  });

  readonly windowWidth = signal(window.innerWidth);
  readonly windowHeight = signal(window.innerHeight);
  readonly isDesktop = computed(() => this.windowWidth() >= 900);
  readonly isMobile = computed(() => this.windowWidth() < 600);

  readonly containerSize = computed((): Rect => {
    const asset = this.activeAsset();
    const sideMenuOpen = !!this.sideMenuMode();
    const windowWidth = this.windowWidth();
    const windowHeight = this.windowHeight();
    if (!asset) return new Rect(0, 0, 0, 0);
    return calcContainerSizeForAsset(asset, windowWidth, windowHeight, sideMenuOpen);
  });

  readonly offsetScale = computed(() => {
    const asset = this.activeAsset();
    const container = this.containerSize();
    if (!asset || !container) return;
    const boundedContainer = this.assetDisplayService.getBoundedContainerSize(asset.version_data, container);
    const { width } = getAssetVersionDimensions(asset.version_data);
    return boundedContainer.width / (width || 1);
  });

  private manuallySetZoomToFit = false;

  readonly scale = linkedSignal<AssetDto, number>({
    source: this.activeAsset,
    computation: asset => {
      const offsetScale = this.offsetScale();
      if (this.manuallySetZoomToFit) return 1;
      if (!asset || offsetScale === undefined) return 0;
      return this.getBaseZoom();
    },
  });

  readonly offset = linkedSignal({
    source: this.activeAsset,
    computation: () => {
      const asset = this.activeAsset();
      if (!asset) return;
      return new Rect();
    },
  });

  readonly crop = computed(() => {
    const asset = this.activeAsset();
    const offset = this.offset();
    const scale = this.scale();
    const offsetScale = this.offsetScale();
    const container = this.containerSize();
    if (!asset || !offset || !container) return;
    const { width, height } = getAssetVersionDimensions(asset.version_data, this.getDataBackup());
    const s = offsetScale * scale;

    return new Rect(
      container.width / 2 - (width * s) / 2 + offset.x * scale,
      container.height / 2 - (height * s) / 2 + offset.y * scale,
      width * s,
      height * s
    );
  });

  readonly commentPlacementMode = signal<'place' | 'drag'>(undefined);
  readonly containerOffset = signal(0);
  readonly animateOffset = signal(false);
  readonly tempDisabledWebGlAnimations = signal(false);
  readonly animationDuration = 200;
  readonly switchingAssetAnimated = signal(false);
  containerVelocity = new Rect();
  previousTouchStartTime = 0;
  readonly emptyRect = new Rect();
  touchStartPos: Rect;
  muted = signal(false);
  clientMode = getFullPath(this.route.snapshot)[0] !== 'a';
  currentLabel = computed(() => {
    const colorLabels = this.colorLabels();
    const activeAsset = this.activeAsset();
    if (!colorLabels || !activeAsset) return;
    return colorLabels.find(l => l.color === activeAsset.color_label);
  });
  readonly controlsVisible = signal(true);
  currentNumberOfTouches = 0;
  isMultiTouchEvent = false;
  readonly touchMode = signal<'normal' | 'swipe'>(undefined);
  touchStartTime: number;
  annotationMode = false;
  createAnnotationShapes: Shape[] = null;
  readonly commentPlaceStartPosition = signal<Rect>(undefined);
  readonly commentPlaceHoverRect = signal<Rect>(undefined);
  readonly commentPlacePosition = signal<Rect>(undefined);
  commentPlacePositionOverridden: boolean;
  annotationTool: AnnotationTool = 'polygon';
  annotationColor: AnnotationColor = 'blue';
  annotationBeingViewed: SimpleAnnotation;
  mobileAnnotationViewingMode = false;
  threadBeingViewedTime: number;
  readonly showThumbnailBar = signal(false);
  platformBrowser = isPlatformBrowser(this.platformId);

  readonly delayedControlsShow = toSignal(timer(150).pipe(map(() => true)));

  private mouseHasMoved = false;
  private mouseIsDown = false;
  readonly longPress = signal(false);
  readonly longPressActiveCache = signal<{ scale: number; offset: Rect }>(undefined);
  readonly longPressMode = signal<'release' | 'downloading' | 'done'>(undefined);
  longPressTimeout: NodeJS.Timeout;
  readonly activePage = signal(0);
  allowedAndEnabledFeedbackOptions = computed(() =>
    this.permissionService.getAllowedAndEnabledFeedbackOptions(
      this.gallery(),
      this.galleryPermissions()?.can_review_if_authenticated
    )
  );
  colorLabels = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    if (this.permissionService.canDisplayElement(billingScopes.asset.customLabels)) {
      return gallery.color_labels;
    }
    return defaultColorLabels;
  });
  private autoHideControls = true;
  private neverHideControls = false;
  private readonly dialogOpen = toSignal(this.dialog.dialog$.pipe(map(d => !!d)));
  videoTime: VideoTimeData;
  videoFocused = signal(false);
  loadingIndicatorTimeout: NodeJS.Timeout;

  readonly showCreateCommentOverlay = signal(false);
  readonly createCommentOverlayPosition = signal<Rect>(undefined);
  readonly commentPlaceAlignment = signal<'left' | 'right' | 'center'>(undefined);
  commentMessage = { text: '', addVideoFramePosition: false, private: false };
  selectedThreadId: string;
  hoverThreadId: string;
  readonly showResolved = signal(false);
  spaceKeyDown = false;
  verticalDragDelta = signal(0);

  private readonly currentAssetUpload$ = combineLatest([
    this.uploadService.uploads$,
    this.activeAsset$.pipe(filter(Boolean)),
  ]).pipe(
    map(
      ([uploads, asset]) =>
        uploads.filter(i => i.status !== 'error' && i.status !== 'done' && i.assetToReplaceId === asset.id)[0]
    )
  );

  readonly newVersionUploadProgress = toSignal(this.currentAssetUpload$.pipe(map(upload => upload?.progress)));
  readonly newVersionUploadingOrProcessing = toSignal(
    this.currentAssetUpload$.pipe(
      pairwise(),
      switchMap(([previous, current]) => {
        const activeAsset = this.activeAsset();
        if (current && current.assetToReplaceId !== activeAsset?.id) return of(undefined);
        if (!previous || current) return of(current);

        const currentVersion = activeAsset.version;
        // wait for new version to be processed, with a timeout incase something goes wrong
        return race([
          this.activeAsset$.pipe(
            filter(a => a.version !== currentVersion),
            take(1)
          ),
          timer(30_000),
        ]).pipe(map(() => undefined));
      })
    )
  );

  private assetChangeSub = this.assetAndGalleryId$
    .pipe(
      takeUntil(this.destroyed$),
      switchMap(({ assetId }) =>
        this.activeAsset$.pipe(
          filter(a => a?.id === assetId),
          take(1)
        )
      )
    )
    .subscribe(asset => {
      if (getFullPath(this.route.snapshot)[0] === 'a') {
        this.unseenActivityService.markAssetAsSeen(asset.id, asset.gallery as string);
      }

      if (asset.type !== 'video') this.videoTime = undefined;

      this.activeVideoEl()?.pause();

      this.backupZoomSettings = undefined;
      if (this.commentPlacementMode() !== 'place') {
        this.commentPlacementMode.set(undefined);
      }
      this.selectedThreadId = undefined;
      this.hoverThreadId = undefined;
      this.commentMessage.text = '';
      this.commentMessage.private = false;
      this.commentMessage.addVideoFramePosition = asset.type === 'video';

      this.activePage.set(0);
      this.disableDragging();

      this.annotationBeingViewed = undefined;
      this.mobileAnnotationViewingMode = false;

      this.containerOffset.set(0);

      this.containerVelocity = new Rect();
      this.assetTransitionService.currentAsset = asset;
      this.switchingAssetAnimated.set(false);
    });

  @HostListener('window:keydown', ['$event'])
  keyDownEvent(event: KeyboardEvent) {
    if (this.dialog.dialog$.value) return;
    const metaKey = isMetaKey(event);

    if (event.code === 'Space') {
      this.spaceKeyDown = true;
      this.check.detectChanges();
    }

    if (event.key === 'Escape') {
      if ((this.commentPlacePosition() && !this.createAnnotationShapes?.length) || this.commentPlacementMode()) {
        if (!this.showCreateCommentOverlay()) {
          this.switchPlaceComment(false);
        }
        this.stopCommentAtPosition();
      } else if (this.selectedThreadId) {
        this.comments?.selectThread(undefined);
      } else if (this.annotationMode) {
        this.stopAnnotation();
        this.analytics.reportKeyboardShortcut('exit-annotation-mode', 'asset-page');
      } else if (this.annotationBeingViewed) {
        this.viewAnnotation(undefined);
        this.analytics.reportKeyboardShortcut('exit-annotation-viewing-mode', 'asset-page');
      } else if (this.showThumbnailBar()) {
        this.showThumbnailBar.set(false);
      } else {
        this.analytics.reportKeyboardShortcut('exit-asset-page', 'asset-page');
        this.close();
      }
      this.check.detectChanges();
    }

    if (event.target['classList'].contains('textarea')) return;
    if (event.target['tagName'] === 'INPUT') return;
    if (event.target['tagName'] === 'TEXTAREA') return;

    if (event.key === 'ArrowRight') {
      if (this.annotationMode) return;
      if (this.videoFocused()) return;
      this.switchAsset(1).subscribe();
    }
    if (event.key === 'ArrowLeft') {
      if (this.annotationMode) return;
      if (this.videoFocused()) return;
      this.switchAsset(-1).subscribe();
    }
    if (event.code === 'Tab') {
      event.preventDefault();
      if (this.annotationMode) return;
      if (this.annotationBeingViewed) return;
      this.showThumbnailBar.set(!this.showThumbnailBar());
      this.analytics.reportKeyboardShortcut('toggle-thumbnails', 'asset-page');
      this.check.detectChanges();
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.annotationMode) return;
      if (this.annotationBeingViewed) return;

      if (this.activeAsset().type === 'document') {
        if (this.activePage() > 0) {
          this.activePage.set(this.activePage() - 1);
        }
      } else {
        this.showThumbnailBar.set(true);
        this.analytics.reportKeyboardShortcut('show-thumbnails', 'asset-page');
        this.check.detectChanges();
      }
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();

      if (this.activeAsset().type === 'document') {
        const pageCount = this.activeAsset().version_data.data?.['images']?.length;
        if (this.activePage() < pageCount - 1) {
          this.activePage.set(this.activePage() + 1);
        }
      } else {
        this.showThumbnailBar.set(false);
        this.analytics.reportKeyboardShortcut('hide-thumbnails', 'asset-page');
        this.check.detectChanges();
      }
    }

    if ((event.key === '=' || event.key === '+') && metaKey) {
      event.preventDefault();
      if (this.activeAsset().type !== 'image' && this.activeAsset().type !== 'document') return;
      if (!canDisplayAsset(this.activeAsset())) return;
      this.onScale('in');
      this.analytics.reportKeyboardShortcut('zoom-in', 'asset-page');
    }
    if (event.key === '-' && metaKey) {
      event.preventDefault();
      if (this.activeAsset().type !== 'image' && this.activeAsset().type !== 'document') return;
      if (!canDisplayAsset(this.activeAsset())) return;
      this.onScale('out');
      this.analytics.reportKeyboardShortcut('zoom-out', 'asset-page');
    }

    if (event.key === '0' && metaKey) {
      if (this.activeAsset().type !== 'image' && this.activeAsset().type !== 'document') return;
      if (!canDisplayAsset(this.activeAsset())) return;
      this.onScale('full');
      this.analytics.reportKeyboardShortcut('zoom-to-full', 'asset-page');
    }

    if (event.key === 'j' && metaKey) {
      this.neverHideControls = true;
      this.showControls();
    }
  }

  @HostListener('window:keyup', ['$event'])
  keyUpEvent(event: KeyboardEvent) {
    if (event.code === 'Space') {
      this.spaceKeyDown = false;
    }
    const metaKey = isMetaKey(event);
    if (event.target['classList'].contains('textarea')) return;
    if (event.target['tagName'] === 'INPUT') return;
    if (event.target['tagName'] === 'TEXTAREA') return;
    if (!metaKey) {
      if (event.key === 'f') {
        this.toggleLike();
        this.analytics.reportKeyboardShortcut('toggle-like', 'asset-page');
      }
      if (event.key === 'a') {
        if (this.activeAsset().status === 'approved') {
          this.updateAssetStatus('no_status', true);
        } else {
          this.updateAssetStatus('approved', true);
        }
      }
      if (event.key === 'x') {
        if (this.activeAsset().status === 'rejected') {
          this.updateAssetStatus('no_status', true);
        } else {
          this.updateAssetStatus('rejected', true);
        }
      }
      if (event.key === 'c') {
        if (this.allowedAndEnabledFeedbackOptions().includes('comment')) {
          this.toggleCommentPlacementMode();
          this.analytics.reportKeyboardShortcut('toggle-comment-placement-mode', 'asset-page');
        }
      }
      if (event.key === 'i' && (this.gallery().save_camera_metadata || this.galleryPermissions().can_edit)) {
        this.toggleSideMenu('info');
        this.analytics.reportKeyboardShortcut('toggle-info-sidebar', 'asset-page');
      }
      if (!this.annotationMode) {
        if (event.key === 'r') {
          this.updateColorLabel('red', true);
        }
        if (event.key === 'o') {
          this.updateColorLabel('orange', true);
        }
        if (event.key === 'g') {
          this.updateColorLabel('green', true);
        }
        if (event.key === 'b') {
          this.updateColorLabel('blue', true);
        }
        if (event.key === 'p') {
          this.updateColorLabel('purple', true);
        }
        if (event.key === 'n') {
          this.updateColorLabel(null, true);
        }
        if (event.key === 'd') {
          if (
            this.activeAsset().type !== 'video' && // not sure why this is turned off for video
            this.activeAsset().type !== 'non_preview' &&
            this.allowedAndEnabledFeedbackOptions().includes('annotation')
          ) {
            this.startAnnotation();
            this.analytics.reportKeyboardShortcut('start-annotation', 'asset-page');
          }
        }
      }
    }
  }

  @HostListener('document:mouseleave', ['$event']) async mouseOut() {
    this.hideControlsInstant();
  }

  readonly fitButton = computed(() => {
    const offsetScale = this.offsetScale();
    const scale = this.scale();
    const asset = this.activeAsset();
    const container = this.containerSize();
    if (!asset || !container) return;
    const zoom = this.assetDisplayService.getBaseZoom(offsetScale, scale);
    return this.assetDisplayService.getButtonFit(asset.version_data, container, zoom, scale);
  });

  readonly canZoom = computed(() => {
    const asset = this.activeAsset();
    if (!asset) return false;
    return ['image', 'document'].includes(asset.type) && canDisplayAsset(asset);
  });

  private readonly closed = signal(false);

  readonly backgroundOpacity = computed(() => {
    const asset = this.activeAsset();
    const offset = this.offset();
    const scale = this.scale();
    if (!isTouchDevice()) return 1;
    if (!asset || !offset) return 1;
    if (scale > 1) return 1;
    if (asset.type === 'document') return 1;
    return 1 - (1 / window.innerHeight) * Math.abs(offset.y * scale);
  });

  @HostListener('window:resize', ['$event']) async winResize() {
    this.windowWidth.set(window.innerWidth);
    this.windowHeight.set(window.innerHeight);
    if (!this.activeAsset()) return;
    this.commentPlacePositionOverridden = false;
    this.calcCommentOverlayPosition();
    this.check.detectChanges();
    this.clampOffset();
  }

  async ngOnInit() {
    if (isPlatformServer(this.platformId)) return;
    this.showControls();
  }

  private enableDragging() {
    this.dragging.set(true);
  }

  private disableDragging() {
    this.dragging.set(false);
    this.clampOffset();
  }

  private getParentTillElementNames(el: HTMLElement, names: string[]) {
    if (names.includes(el.nodeName.toLowerCase())) return true;
    if (!el.parentElement) return false;
    return this.getParentTillElementNames(el.parentElement, names);
  }

  onWheel(event: WheelEvent) {
    if (this.getParentTillElementNames(event.target as HTMLElement, ['app-pdf-page-list'])) return;
    if (this.activeAsset().type !== 'image') return;
    if (!canDisplayAsset(this.activeAsset())) return;
    event.preventDefault();

    this.removeLongPress();

    const { scale, offsetChange } = this.assetDisplayService.onWheel(
      event,
      this.containerSize(),
      this.scale(),
      this.offsetScale()
    );

    this.scale.set(scale);
    this.offset.update(offset => offset.add(offsetChange));
    this.clampOffset();
    this.onScale(undefined);
    this.check.detectChanges();
    return false;
  }

  public getScalePercent() {
    return Math.round(this.offsetScale() * 100 * this.scale() * devicePixelRatio());
  }

  private tempDisableWebGl() {
    this.tempDisabledWebGlAnimations.set(true);
    setTimeout(() => this.tempDisabledWebGlAnimations.set(false), this.animationDuration);
  }

  onScale(dir: string, dontAnimate = false) {
    this.tempDisableWebGl();
    this.scale.set(
      this.assetDisplayService.dirToScale(
        this.activeAsset().version_data,
        dir,
        this.containerSize(),
        this.scale(),
        this.offsetScale()
      ) || this.scale()
    );

    if (dir === 'height' || dir === 'width') {
      this.offset.set(new Rect());
    }
    if (dir && !dontAnimate) {
      this.animateOffset.set(true);
      setTimeout(() => {
        this.animateOffset.set(false);
      }, this.animationDuration);
    }
    this.scale.set(this.assetDisplayService.clipScale(this.scale(), this.offsetScale()));
    this.clampOffset();
    if (dir) this.manuallySetZoomToFit = this.scale() === 1;
  }

  private clampOffset() {
    const asset = this.activeAsset();
    const scale = this.scale();
    const container = this.containerSize();
    const dragging = this.dragging();

    if (!asset || !container) return;
    if (dragging) return;

    if (this.scale() <= 1) {
      this.offset.set(new Rect());
    }

    const { left, right, top, bottom } = this.assetDisplayService.getScaledBounds(
      asset.version_data,
      container,
      this.offset(),
      this.scale()
    );

    if (left < 0) this.offset.update(offset => offset.add(new Rect(left / scale)));
    else if (right < 0) this.offset.update(offset => offset.subtract(new Rect(right / scale)));

    if (asset.type !== 'document') {
      if (top < 0) this.offset.update(offset => offset.add(new Rect(0, top / scale)));
      else if (bottom < 0) this.offset.update(offset => offset.subtract(new Rect(0, bottom / scale)));
    }
  }

  close() {
    this.closed.set(true);
    if (this.route.snapshot.queryParams.returnTo) {
      this.router.navigateByUrl(this.route.snapshot.queryParams.returnTo);
    } else {
      if (this.location.getState()['navigationId'] !== 1) {
        this.location.back();
      } else {
        this.router.navigate(['../'], { relativeTo: this.route });
      }
    }
  }

  private showControls(autoHide = true) {
    if (this.sideMenuMode() && !this.isDesktop()) return;
    this.controlsVisible.set(true);
    this.autoHideControls = autoHide;
    this.hideControls();
  }

  private touchToggleControls(event?: TouchEvent) {
    if (this.sideMenuMode() && !this.isDesktop() && event) this.touchToggleControlsInstant(event);
    else this.touchToggleControlsDebounced(event);
  }

  private touchToggleControlsDebounced = debounce(
    (event?: TouchEvent) => this.touchToggleControlsInstant(event),
    doubleClickTime
  );

  private touchToggleControlsInstant(event: TouchEvent) {
    if (!event) return;
    const pos = Rect.fromTouch(event.changedTouches[0]);
    if (!this.touchStartPos) return;
    if (this.longPress()) return;
    const diff = pos.subtract(this.touchStartPos);
    if (diff.magnitude() > 2) return;
    this.controlsVisible.set(!this.controlsVisible());

    if (!!this.sideMenuMode() && !this.isDesktop() && event) {
      this.toggleSideMenu(this.sideMenuMode());
    }
  }

  private hideControlsInstant() {
    if (!this.isDesktop()) return;
    if (this.sideMenuMode()) return;
    if (this.neverHideControls) return;
    if (this.dialogOpen()) return;
    if (this.contextService.openContextMenus$.value?.length) return this.hideControls();
    this.controlsVisible.set(false);
  }

  private hideControls = debounce(() => {
    if (!this.autoHideControls) return;
    this.hideControlsInstant();
  }, 2500);

  private handleEndCommentDragMode() {
    if (this.controlsVisible() && isTouchDevice()) {
      this.controlsVisible.set(false);
    }
    if (this.commentPlaceHoverRect()) {
      const size = new Rect(this.commentPlaceHoverRect().width, this.commentPlaceHoverRect().height).magnitude();
      if (size < 10) {
        this.commentPlaceHoverRect.update(rect => new Rect(rect.x, rect.y));
      }
      this.analytics.track('comment-placed', false, {
        type: 'rect',
      });
      return this.startCommentAtPosition(this.commentPlaceHoverRect());
    }
    this.analytics.track('comment-placed', false, {
      type: 'point',
    });
    return this.startCommentAtPosition(this.commentPlaceStartPosition());
  }

  mouseDown(event: MouseEvent) {
    if (event.buttons !== 1 && event.buttons !== 4) return;
    if (event['sourceCapabilities']?.['firesTouchEvents']) return;

    if (this.commentPlacementMode() && !this.spaceKeyDown && event.buttons !== 4) {
      return this.setCommentPlaceDrag(Rect.fromMouseEvent(event));
    }

    const path = getEventPath(event);
    this.showControls(!path.find(el => el.classList?.contains('dont-hide-controls')));
    this.touchStartTime = Date.now();
    this.mouseIsDown = true;
    if (!elementIsInputLike(document.activeElement) && !elementIsInputLike(<Element>event.target)) {
      event.preventDefault(); // needed for firefox dragging to work
    }
    this.enableDragging();
    this.mouseHasMoved = false;
    this.check.detectChanges();
  }

  mouseUp(event: MouseEvent) {
    if (this.commentPlacementMode() === 'drag') {
      return this.handleEndCommentDragMode();
    }

    if (!this.mouseIsDown) return;
    this.mouseIsDown = false;
    this.disableDragging();
    const path = getEventPath(event);

    this.check.detectChanges();

    if (path.find(el => el instanceof HTMLButtonElement)) return;
    if (['image', 'document'].includes(this.activeAsset().type) && canDisplayAsset(this.activeAsset())) {
      const touchTime = Date.now() - this.touchStartTime;
      if (!this.mouseHasMoved && touchTime < 200) {
        if (this.selectedThreadId) {
          this.comments?.selectThread(undefined);
          return;
        }

        if (this.scale() <= 1) {
          const container = this.containerSize();
          const offsetX = (event.clientX - container.width / 2) / this.scale();
          const offsetY = (event.clientY - container.height / 2) / this.scale();
          this.offset.update(offset => offset.subtract(new Rect(offsetX, offsetY)));
        }
        this.onScale('full');
      }
    } else {
      this.resetOffsetAnimated().subscribe();
    }
  }

  mouseMove(event: MouseEvent) {
    // if (!document.hasFocus()) return;
    if (event['sourceCapabilities']?.['firesTouchEvents']) return;
    if (isTouchDevice()) return;
    if (!this.isDesktop() && this.commentPlacementMode() === 'place') return;
    if (this.commentPlacementMode() === 'drag') {
      const pos = Rect.fromMouseEvent(event);
      const bounds = this.commentPlaceStartPosition().union(pos);
      if (new Rect(bounds.width, bounds.height).magnitude() < 10) return;
      this.commentPlaceHoverRect.set(bounds);
      return;
    }

    const path = getEventPath(event);
    this.showControls(!path.find(el => el.classList?.contains('dont-hide-controls')));
    if (!this.mouseIsDown) return;

    if (event.buttons === 4 && !this.dragging()) this.enableDragging();

    if (event.buttons !== 1 && event.buttons !== 4) {
      if (this.dragging()) this.mouseUp(event);
      return;
    }
    if (this.activeAsset().type !== 'image' && this.activeAsset().type !== 'document') return;
    if (!canDisplayAsset(this.activeAsset())) return;
    const scale = this.scale();

    this.offset.update(offset => {
      return offset.add(new Rect(event.movementX * 1, event.movementY * 1).times(1 / scale));
    });

    if (Math.abs(event.movementX) > 2 || Math.abs(event.movementY) > 2) {
      this.mouseHasMoved = true;
    }
    this.verticalDragDelta.set(event.movementY * 1);
  }

  touchStart(event: TouchEvent) {
    if (this.switchingAssetAnimated()) return;
    if (this.commentPlacementMode()) {
      return this.setCommentPlaceDrag(Rect.fromTouchEvent(event)[0]);
    }

    this.touchMode.set(this.scale() > 1 ? 'normal' : undefined);
    this.currentNumberOfTouches = event.touches.length;
    this.isMultiTouchEvent = this.isMultiTouchEvent || this.currentNumberOfTouches > 1;
    this.previousTouchStartTime = this.touchStartTime;
    this.touchStartPos = Rect.fromTouchEvent(event)[0];
    this.previousTouches = Rect.fromTouchEvent(event);
    this.touchStartTime = Date.now();

    if (this.allowLongTouchDownload()) {
      this.longPress.set(false);
      this.longPressActiveCache.set(undefined);
      this.longPressTimeout = setTimeout(() => {
        this.longPress.set(true);
        this.longPressMode.set('release');
        this.longPressActiveCache.set({
          scale: this.scale(),
          offset: this.offset().copy(),
        });
        this.scale.set(0.9);
        this.offset.set(new Rect(0, 0));
      }, longPressDuration);
    } else if (this.longPressTimeout) {
      this.removeLongPress();
    }
  }

  touchMove(event: TouchEvent) {
    if (this.activeAsset().type !== 'document') {
      event.preventDefault();
    }
    this.removeLongPress();
    const pos = Rect.fromTouchEvent(event)[0];
    if (this.commentPlacementMode() === 'drag') {
      const bounds = this.commentPlaceStartPosition().union(pos);
      if (new Rect(bounds.width, bounds.height).magnitude() < 10) return;
      this.commentPlaceHoverRect.set(bounds);
      return;
    }
    this.enableDragging();
    if (this.previousTouches?.length) {
      if (event.touches.length === 1) {
        if (this.previousTouches.length > 1) {
          // Without this the asset jumps around because previoustouches[0] might not be the same finger as event.touches[0]
          this.previousTouches = Rect.fromTouchEvent(event);
        }
        this.touchMoveSingle(event);
      } else this.touchMoveMultiple(event);
    }
    this.previousTouches = Rect.fromTouchEvent(event);

    if (this.touchMode() === 'swipe') {
      this.offset.update(o => new Rect(o.x, 0));
    } else if (!this.touchMode()) {
      const movement = pos.subtract(this.touchStartPos);
      if (movement.magnitude() > 10) {
        const x = Math.floor(Math.abs(movement.x));
        const y = Math.floor(Math.abs(movement.y));
        this.touchMode.set(x > y * 2 ? 'swipe' : 'normal');
      }
    }
  }

  private touchMoveSingle(event: TouchEvent) {
    if (this.annotationMode) return;
    const touch = Rect.fromTouch(event.touches[0]);

    const { zoomedIn, offsetChange, diff } = this.assetDisplayService.touchMoveSingle(
      this.offsetScale(),
      this.scale(),
      touch,
      this.previousTouches[0]
    );

    if (zoomedIn) {
      this.offset.update(o => o.add(new Rect(offsetChange.x)));
    } else {
      if (this.mobileAnnotationViewingMode) return;
      this.containerVelocity = new Rect(diff[0], diff[1]);
      this.containerOffset.update(o => o + diff[0]);

      // if (!this.touchDragDirMode()) {
      //   if (Math.abs(this.containerOffset()) > 10) this.touchDragDirMode.set('x');
      //   if (Math.abs(this.offset().y) > 20) this.touchDragDirMode.set('y');
      // }
    }

    this.offset.update(o => o.add(new Rect(0, offsetChange.y)));
    if (!isTouchDevice()) {
      this.verticalDragDelta.set(diff[1]);
    } else {
      this.verticalDragDelta.set(0);
    }
  }

  private touchMoveMultiple(event: TouchEvent) {
    if (!this.isDesktop() && this.sideMenuMode()) return;
    const touches = Rect.fromTouchEvent(event);
    if (!touches.length || this.previousTouches.length < 2) return;

    const { offsetChange, scale } = this.assetDisplayService.touchMoveMultiple(
      event,
      this.previousTouches,
      this.scale(),
      this.offsetScale()
    );

    this.offset.update(offset => offset.add(offsetChange));
    this.scale.set(scale);
  }

  private isDoubleTap(event: TouchEvent) {
    if (this.activeAsset().type !== 'image' && this.activeAsset().type !== 'document') return false;
    if (!canDisplayAsset(this.activeAsset())) return false;
    if (this.currentNumberOfTouches !== 0) return false;
    if (!this.previousTouchStartTime) return false;
    if (Date.now() - this.previousTouchStartTime > doubleClickTime) return false;
    const touchMoved = this.touchStartPos
      ? Rect.fromTouch(event.changedTouches[0]).subtract(this.touchStartPos)
      : new Rect();
    if (touchMoved.magnitude() > 20) return false;
    const touchTime = Date.now() - this.touchStartTime;
    if (touchTime > 200) return false;
    return true;
  }

  async touchEnd(event: TouchEvent) {
    const clickOnDocControls = getTagFromEvent(event, 'app-document-viewer-controls');
    if (clickOnDocControls) return;

    if (this.commentPlacementMode() === 'drag') {
      return this.handleEndCommentDragMode();
    }
    const action = this.getTouchEndAction(event);

    if (action === 'close') {
      return this.close();
    }

    this.currentNumberOfTouches = event.touches.length;
    if (this.currentNumberOfTouches > 0) return;
    this.previousTouches = [];
    this.isMultiTouchEvent = false;
    this.disableDragging();

    if (action !== 'download') this.removeLongPress();
    if (action === 'download') {
      this.longPressMode.set(undefined);

      if (showIosDownloadMenu(this.gallery(), this.galleryPermissions(), this.activeAsset())) {
        return this.dialog
          .open(IosDownloadDialogComponent, {
            galleryId: this.gallery().id,
            assetId: this.activeAsset().id,
            versionId: this.activeAsset().version,
          })
          .then(() => {
            this.removeLongPress();
          });
      } else {
        this.downloadAssetService
          .autoDownloadAsset(this.activeAsset(), this.activeAsset().version_data, this.gallery(), 'auto')
          .subscribe(res => {
            if (res === 'downloading') {
              this.longPressMode.set('downloading');
            } else if (res === 'downloaded') {
              this.longPressMode.set(undefined);
            } else {
              this.removeLongPress();
              if (res === true) {
                this.longPressMode.set('done');
                setTimeout(() => this.longPressMode.set(undefined), 1000);
              } else {
                this.longPressMode.set(undefined);
              }
            }
          });
      }
    } else if (action === 'switch-right') {
      this.switchAssetAnimated(1).subscribe();
    } else if (action === 'switch-left') {
      this.switchAssetAnimated(-1).subscribe();
    } else {
      this.resetOffsetAnimated().subscribe();
      const path = getEventPath(event);
      const touchedVideoWithControls =
        (path.some(el => el.tagName === 'APP-VIDEO-VIEWER') ||
          path.some(el => el.tagName === 'APP-AUDIO-VIEWER') ||
          path.some(el => el.tagName === 'APP-ASSET-DOWNLOAD-BUTTON')) &&
        this.controlsVisible();
      if (!touchedVideoWithControls) {
        this.touchToggleControls(event);
      }
    }

    const disableZoomMobileSidemenu = !this.isDesktop() && this.sideMenuMode();
    if (this.isDoubleTap(event) && !disableZoomMobileSidemenu && !this.annotationMode) {
      this.touchToggleControls(undefined); // prevent double tap from showing controls

      if (this.scale() <= 1) {
        const container = this.containerSize();
        const offsetX = this.touchStartPos.x - container.width / 2;
        const offsetY = this.touchStartPos.y - container.height / 2;
        this.offset.set(new Rect(-offsetX, -offsetY));
      }

      this.onScale('full');
      this.offset.set(new Rect());
      this.containerOffset.set(0);
    }
  }

  private getTouchEndAction(event: TouchEvent) {
    if (this.annotationMode) return;
    if (this.mobileAnnotationViewingMode) return;
    if (!this.touchStartPos) return;
    if (this.longPress()) return 'download';
    const touchMoved = Rect.fromTouch(event.changedTouches[0]).subtract(this.touchStartPos);

    if (this.scale() > 1) return;
    if (this.touchMode() === 'swipe') {
      if (Math.abs(this.containerVelocity.x) > 3 || Math.abs(touchMoved.x) > window.innerWidth / 2) {
        return touchMoved.x > 0 ? 'switch-left' : 'switch-right';
      }
    } else {
      if (this.activeAsset().type === 'document') return;
      // pinch zoom to close
      if (this.scale() < this.getBaseZoom() / 1.3) return 'close';

      if (this.isMultiTouchEvent) return;
      if (!this.touchMode()) return;
      const velocityInSameDirectionAsTouchMoved = this.containerVelocity.y * touchMoved.y > 0;
      if (
        (velocityInSameDirectionAsTouchMoved && Math.abs(this.containerVelocity.y) > 3) ||
        Math.abs(touchMoved.y) > window.innerHeight / 4
      ) {
        return 'close';
      }
    }
  }

  private canOpenSideMenu(mode: 'info' | 'activity') {
    if (mode === null) return true;
    if (mode === 'activity') {
      return (
        this.galleryPermissions()?.can_review_if_authenticated &&
        this.allowedAndEnabledFeedbackOptions()?.includes('comment')
      );
    }

    if (mode === 'info') {
      return this.galleryPermissions()?.can_edit || !!this.gallery()?.save_camera_metadata;
    }
  }

  private setSideMenu(mode: 'info' | 'activity' | undefined) {
    this.storage.set('asset-sidebar', mode);
    if (!this.canOpenSideMenu(mode)) return;
    this.router.navigate([], {
      queryParams: {
        menu: mode || null,
      },
      replaceUrl: true,
    });
  }

  async toggleSideMenu(mode: 'info' | 'activity' | undefined) {
    if (!this.canOpenSideMenu(mode)) {
      return this.setSideMenu(null);
    }
    if (this.annotationBeingViewed) {
      this.viewAnnotation(undefined);
    }
    if (this.closed()) return;
    if (this.sideMenuMode() === mode) mode = null;

    if (this.commentPlacementMode() !== 'place') {
      this.commentPlacementMode.set(undefined);
    }
    this.commentPlacePositionOverridden = false;
    this.selectedThreadId = undefined;
    this.hoverThreadId = undefined;
    this.calcCommentOverlayPosition();
    this.check.detectChanges();

    this.setSideMenu(mode);
    if (!this.isDesktop()) {
      if (mode) {
        this.controlsVisible.set(false);
        this.onScale('fit', true);
      } else {
        this.controlsVisible.set(true);
      }
    }
  }

  private getBaseZoom() {
    const asset = this.activeAsset();
    if (asset.type !== 'image' && asset.type !== 'document') return baseZoom;
    if (!canDisplayAsset(asset)) return baseZoom;
    return this.assetDisplayService.getBaseZoom(this.offsetScale(), baseZoom);
  }

  async onSwitchToAsset(assetId: string) {
    this.router.navigate(['../' + assetId], {
      relativeTo: this.route,
      fragment: this.route.snapshot.fragment,
      replaceUrl: true,
      queryParamsHandling: 'preserve',
    });
  }

  private getSwitchToAsset(change: 1 | -1) {
    if (this.loadingMoreAssets()) return of(undefined);
    return forkJoin([
      this.assetList$.pipe(filter(Boolean), take(1)),
      this.activeAsset$.pipe(filter(Boolean), take(1)),
    ]).pipe(
      map(([assets, asset]) => {
        if (!assets || !asset) return;
        const index = assets.findIndex(a => a.id === asset.id);
        return assets[index + change];
      })
    );
  }

  switchAsset(change: 1 | -1) {
    return this.getSwitchToAsset(change).pipe(
      switchMap(asset => {
        if (!asset) return of(false);
        return from(this.changeToAsset(asset.id)).pipe(map(() => true));
      })
    );
  }

  private resetOffsetAnimated() {
    this.animateOffset.set(true);
    if (this.scale() <= 1) {
      this.offset.set(new Rect());
      this.scale.set(this.getBaseZoom());
    } else {
      this.clampOffset();
    }
    this.containerOffset.set(0);
    return timer(this.animationDuration).pipe(
      tap(() => {
        this.animateOffset.set(false);
      })
    );
  }

  private switchAssetAnimated(change: 1 | -1) {
    return this.getSwitchToAsset(change).pipe(
      delay(1), // prevent animating the secondary assets when they load in
      switchMap(asset => {
        if (!asset) return this.resetOffsetAnimated();
        this.switchingAssetAnimated.set(true);
        this.animateOffset.set(true);
        this.offset.set(new Rect());
        if (change === 1) {
          this.containerOffset.set(-this.containerSize().width);
        } else {
          this.containerOffset.set(this.containerSize().width);
        }
        return timer(this.animationDuration).pipe(
          switchMap(() => {
            this.animateOffset.set(false);
            return this.switchAsset(change);
          })
        );
      })
    );
  }

  async changeToAsset(assetId: string) {
    const queryParams = copy(this.route.snapshot.queryParams);
    delete queryParams.autoplay;
    await this.router.navigate(['../' + assetId], {
      relativeTo: this.route,
      fragment: this.route.snapshot.fragment,
      replaceUrl: true,
      queryParams: queryParams,
    });
  }

  async updateAssetStatus(status: AssetStatus, isKeyboardShortcut = false) {
    if (this.gallery().enabled_asset_statuses && !this.gallery().enabled_asset_statuses.includes(status)) {
      if (status === 'rejected') status = 'no_status';
      else return;
    }
    if (isKeyboardShortcut) {
      this.analytics.reportKeyboardShortcut('asset-status', 'asset-page', { status });
    }
    return this.feedbackService.updateStatus([this.activeAsset()], this.gallery(), status);
  }

  async updateColorLabel(color_label: AssetColorLabel, isKeyboardShortcut = false) {
    if (isKeyboardShortcut) {
      const colorLabelName = this.gallery().color_labels?.find(l => l.color === color_label)?.name;
      this.analytics.reportKeyboardShortcut('color-label', 'asset-page', { color: color_label, colorLabelName });
    }
    await this.feedbackService.updateColorLabel([this.activeAsset()], this.gallery(), color_label);
    this.check.detectChanges();
  }

  async toggleLike() {
    if (this.activeAsset().is_liked) {
      await this.feedbackService.unlikeAsssets([this.activeAsset()], this.gallery());
    } else {
      await this.feedbackService.likeAsssets([this.activeAsset()], this.gallery());
    }
  }

  toggleCommentPlacementMode() {
    if (this.commentPlacementMode()) {
      this.commentPlacementMode.set(undefined);
      this.commentPlacePosition.set(undefined);
      this.commentPlacePositionOverridden = false;
      this.check.detectChanges();
    } else {
      this.switchPlaceComment(true);
    }
  }

  createCommentOverlayClose() {
    this.stopCommentAtPosition();
    if (!this.isDesktop() && this.commentPlacementMode()) {
      // on mobile we want to get out of comment placing mode when the overlay is closed
      this.toggleCommentPlacementMode();
    }
  }

  async startAnnotation() {
    if (!this.allowedAndEnabledFeedbackOptions().includes('annotation')) return;
    if (this.commentPlacementMode() || this.commentPlacePosition()) {
      this.switchPlaceComment(false);
      this.stopCommentAtPosition();
    }
    this.annotationMode = true;
    this.createAnnotationShapes = null;
    this.annotationTool = 'polygon';
    this.annotationColor = 'blue';
    this.showThumbnailBar.set(false);
    this.annotationBeingViewed = undefined;
    this.mobileAnnotationViewingMode = false;

    if (!this.isDesktop()) {
      this.toggleSideMenu(undefined);
    }

    if (this.activeAsset().type === 'video') {
      this.videoTimeService.setTime(this.videoTime.currentSecond);
    }
    this.check.detectChanges();
  }

  async stopAnnotation() {
    this.annotationMode = false;
    this.createAnnotationShapes = null;
    this.showCreateCommentOverlay.set(false);
    this.commentPlacePosition.set(undefined);
    this.commentPlacePositionOverridden = false;
    this.showControls();
    this.check.detectChanges();
  }

  toggleAnnotationMode() {
    if (this.annotationMode) this.stopAnnotation();
    else this.startAnnotation();
  }

  public annotationDone(send: boolean) {
    if (!send) return this.stopAnnotation();
    this.showCreateCommentOverlay.set(true);
    this.check.detectChanges();
  }

  annotationMouseDown() {
    this.showCreateCommentOverlay.set(false);
    this.check.detectChanges();
  }

  annotationShapesChanges(shapes: Shape[]) {
    this.createAnnotationShapes = shapes;
    if (shapes?.length) {
      if (this.isDesktop()) {
        this.showCreateCommentOverlay.set(true);
        this.commentPlacePosition.set(
          getShapeCommentPosition(
            shapes,
            this.activeAsset(),
            this.offsetScale(),
            this.scale(),
            this.offset(),
            !!this.sideMenuMode(),
            true,
            this.containerSize().width,
            400
          )
        );
        this.calcCommentOverlayPosition();
      } else {
        // on mobile we always put the avatar in the center of the annotation
        const bounds = getAnnotationBounds(shapes);
        bounds.x += bounds.width / 2;
        bounds.y += bounds.height / 2;
        this.commentPlacePosition.set(
          relativeToAbsolutePosition(
            bounds,
            this.activeAsset(),
            this.offsetScale(),
            this.scale(),
            this.offset(),
            !!this.sideMenuMode(),
            false
          )
        );
      }
    } else {
      this.showCreateCommentOverlay.set(false);
    }
    this.check.detectChanges();
  }

  private setCommentPlaceDrag(position: Rect) {
    this.commentPlacementMode.set('drag');
    this.commentPlaceStartPosition.set(position);
    this.commentPlaceHoverRect.set(undefined);
  }

  private startCommentAtPosition(position: Rect) {
    this.showCreateCommentOverlay.set(true);
    this.commentPlacementMode.set('place');
    this.commentPlacePosition.set(position);
    this.calcCommentOverlayPosition();
    this.check.detectChanges();
  }

  stopCommentAtPosition() {
    this.showCreateCommentOverlay.set(false);
    this.commentPlaceStartPosition.set(undefined);
    this.commentPlaceHoverRect.set(undefined);
    this.check.detectChanges();
  }

  private calcCommentOverlayPosition() {
    if (!this.showCreateCommentOverlay() || !this.commentPlacePosition()) return;
    const { pos, align } = getPointCommentPosition(this.commentPlacePosition(), this.containerSize().width, 400);

    if (align === 'left') {
      this.createCommentOverlayPosition.set(pos.add(new Rect(-23, -19)));
    } else if (align === 'right') {
      this.createCommentOverlayPosition.set(pos.add(new Rect(23, -19)));
    } else {
      this.createCommentOverlayPosition.set(pos.add(new Rect(0, -19)));
    }
    this.commentPlaceAlignment.set(align);
  }

  async onNewThread(thread: CommentThread) {
    if (!this.sideMenuMode() || this.sideMenuMode() !== 'activity') {
      this.toggleSideMenu('activity');
      await sleep(0);
    }
    this.comments?.selectThread(thread);
    this.check.detectChanges();
  }

  async switchPlaceComment(enabled: boolean) {
    if (enabled) {
      this.activeVideoEl()?.pause();
      if (this.annotationMode) {
        this.stopAnnotation();
      }
    }
    if (enabled && !this.isDesktop()) {
      await this.toggleSideMenu(undefined);
      this.controlsVisible.set(false);
    }

    this.commentPlacementMode.set(enabled ? 'place' : undefined);
    this.commentPlacePosition.set(undefined);
    this.commentPlacePositionOverridden = false;
    this.check.detectChanges();
  }

  async viewAnnotation(annotation: SimpleAnnotation) {
    if (annotation) {
      if (!this.videoTime && this.activeAsset().type === 'video') {
        await this.activeVideoEl()?.initialPlayClick();
        await sleep(0);
        this.activeVideoEl()?.pause();
      }
      this.stopAnnotation();
      this.analytics.track('view_annotation');
      this.zoomToAnnotation(annotation);
      if (annotation.frame && this.videoTime) {
        const time = this.videoTime.durationSeconds * annotation.frame;
        this.videoTimeService.setTime(time);
        this.threadBeingViewedTime = undefined;
        this.check.detectChanges();
        firstValueFrom(this.waitTillVideoTimeIsSetTo(time)).then(() => {
          this.threadBeingViewedTime = time;
          this.check.detectChanges();
        });
      }
      if (annotation.frame && this.activeAsset().type === 'document') {
        this.activePage.set(annotation.frame);
      }
    } else {
      this.restoreBackupZoomSettings();
      this.threadBeingViewedTime = undefined;
      this.analytics.track('hide_annotation');
      if (this.mobileAnnotationViewingMode) {
        this.toggleSideMenu('activity');
      }
    }
    this.annotationBeingViewed = annotation;
    this.check.detectChanges();
  }

  private restoreBackupZoomSettings() {
    if (this.backupZoomSettings) {
      this.scale.set(this.backupZoomSettings.scale);
      this.offset.set(this.backupZoomSettings.offset.copy());
      this.backupZoomSettings = undefined;
    }
  }

  private zoomToAnnotation(annotation: SimpleAnnotation) {
    const timeAgo = new Date().getTime() - annotation.created_at * 1000;
    if (timeAgo < 1000) return;
    this.zoomToBounds(getAnnotationBounds(annotation.shapes));
  }

  public zoomToComment(thread: CommentThread) {
    if (!thread) {
      return this.restoreBackupZoomSettings();
    }
    const timeAgo = new Date().getTime() - thread.created_at * 1000;
    if (timeAgo < 1000) return;
    const x = Array.isArray(thread.position.x) ? thread.position.x : [thread.position.x];
    const y = Array.isArray(thread.position.y) ? thread.position.y : [thread.position.y];
    const rects = x.map((x, i) => new Rect(x, y[i]));
    this.zoomToBounds(getAnnotationBounds(rects));
  }

  private zoomToBounds(bounds: Rect) {
    if (this.activeAsset().type !== 'image') return;
    if (!this.backupZoomSettings) {
      this.backupZoomSettings = {
        scale: this.scale(),
        offset: this.offset().copy(),
      };
    }
    const boundedContainer = this.assetDisplayService.getBoundedContainerSize(
      this.activeAsset().version_data,
      this.containerSize(),
      this.activePage()
    );

    bounds.x *= boundedContainer.width;
    bounds.y *= boundedContainer.height;
    bounds.width *= boundedContainer.width;
    bounds.height *= boundedContainer.height;

    const scale = Math.min(boundedContainer.width / bounds.width, boundedContainer.height / bounds.height);

    const offsetX = bounds.x + bounds.width / 2 - boundedContainer.width / 2;
    const offsetY = bounds.y + bounds.height / 2 - boundedContainer.height / 2;

    this.scale.set(Math.min(scale * 0.9, 2));
    this.offset.set(new Rect(-offsetX, -offsetY));
  }

  private waitTillVideoTimeIsSetTo(time: number) {
    return of(null).pipe(
      switchMap(() => {
        if (Math.abs(this.videoTime.currentSecond - time) > 0.001) return throwError(() => 'time not set');
        return of(true);
      }),
      retry({ count: 20, delay: 100 }),
      catchError(() => of(null))
    );
  }

  async videoTimeChangeWithoutAnnotation(fraction: number) {
    if (!this.videoTime && this.activeAsset().type === 'video') {
      await this.activeVideoEl()?.initialPlayClick();
      await sleep(0);
      this.activeVideoEl()?.pause();
    }
    const time = this.videoTime.durationSeconds * fraction;
    this.videoTimeService.setTime(time);
    this.threadBeingViewedTime = undefined;
    this.check.detectChanges();
    firstValueFrom(this.waitTillVideoTimeIsSetTo(time)).then(() => {
      this.threadBeingViewedTime = time;
      this.check.detectChanges();
    });
  }

  toggleThumbnailBar(open: boolean) {
    this.showThumbnailBar.set(open);
    this.check.detectChanges();
  }

  onPreviewMove(offset: Rect) {
    this.offset.set(offset);
    this.check.detectChanges();
  }

  async uploadVersion(_files: File[]) {
    if (!this.galleryPermissions().can_edit) return;
    const files = Array.from(_files);
    if (!files.length) return;
    if (files.length > 1) return this.toasts.add('Please upload only one asset', 'error');
    const file = files[0];
    const resp = await this.uploadService.handleCanUpload([file]);
    if (!resp) return;

    const asset = this.activeAsset();
    await this.uploadService.uploadNewVersion(asset, file);
    if (this.activeAsset().id !== asset.id) return;
    await this.uploadService.waitForAssetProcessing(asset.id);
  }

  private allowDownload(asset: AssetDto) {
    if (this.galleryPermissions().can_edit) return true;
    if (!this.gallery().download_single_asset) return false;
    if (this.gallery().approved_downloads_only) {
      return asset.status === 'approved';
    }
    return true;
  }

  private allowLongTouchDownload() {
    if (!IsIos()) return false;
    if (this.currentNumberOfTouches !== 1) return false;
    if (this.annotationMode) return false;
    if (!this.allowDownload(this.activeAsset())) return false;
    if (!['image', 'video'].includes(this.activeAsset()?.type)) return false;
    if (!canDisplayAsset(this.activeAsset())) return false;
    return true;
  }

  getDataBackup() {
    return { width: 250 * devicePixelRatio(), height: 250 * devicePixelRatio() };
  }

  onTimeChange(time: VideoTimeData) {
    if (this.videoTime && objectsMatch(this.videoTime, time)) return;
    this.videoTime = time;

    if (this.threadBeingViewedTime) {
      const timeChanged = Math.abs(this.threadBeingViewedTime - time.currentSecond) > 0.001;
      if (timeChanged) {
        if (this.selectedThreadId) {
          this.comments?.selectThread(undefined);
          this.annotationBeingViewed = undefined;
          this.mobileAnnotationViewingMode = false;
        }
      }
    }
    this.check.detectChanges();
  }

  async selectThreadFromFace(thread: CommentThread) {
    if (!this.sideMenuMode() || this.sideMenuMode() !== 'activity') {
      this.toggleSideMenu('activity');
    }
    await sleep(0);
    this.comments?.toggleSelectedThread(thread);
    this.check.detectChanges();
  }

  public get showAnnotationControls() {
    if (!this.annotationMode) return false;
    if (this.isDesktop()) return true;
    return !this.showCreateCommentOverlay();
  }

  async onOffsetChange(offsetY: number) {
    this.offset.update(offset => new Rect(offset.x, offsetY / this.scale()));
  }

  private removeLongPress() {
    if (!this.longPressTimeout) return;
    clearTimeout(this.longPressTimeout);
    this.longPressTimeout = undefined;
    this.longPress.set(false);
    this.longPressMode.set(undefined);
    if (this.longPressActiveCache()) {
      const c = this.longPressActiveCache();
      this.offset.set(c.offset.copy());
      this.scale.set(c.scale);
      this.longPressActiveCache.set(undefined);
    }
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
